import React, { useState } from 'react';
import CustomButton from './CustomButton';
import { api } from '../services/api';
import { useRegister } from '../context/RegisterContext';
import './StepConfirmacao.css';

interface Props {
  onBack: () => void;
}

const StepConfirmacao: React.FC<Props> = ({ onBack }) => {
  const { formData } = useRegister();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleCadastrar = async () => {
    setLoading(true);
    setError('');

    try {
      await api.createPetshopUser({
        user: {
          full_name: formData.responsavel.nomeCompleto,
          email: formData.login.email,
          document: formData.responsavel.cpf.replace(/\D/g, ''),
          password: formData.login.password,
          phone: formData.responsavel.telefone.replace(/\D/g, ''),
        },
        petshop: {
          pet_shop_name: formData.negocio.nomePetshop,
          company_name: formData.negocio.razaoSocial,
          cnpj: formData.negocio.cnpj.replace(/\D/g, ''),
          phone: formData.negocio.telefone.replace(/\D/g, ''),
          email: formData.responsavel.email,
          category_id: formData.category.id ?? null,
          address: {
            zip_code: formData.endereco.cep.replace(/\D/g, ''),
            street: formData.endereco.rua,
            number: formData.endereco.numero,
            complement: formData.endereco.complemento,
            neighborhood: formData.endereco.bairro,
            city: formData.endereco.cidade,
            state: formData.endereco.uf,
          },
        },
      });
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'cadastro_final' });
      setSuccess(true);
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro ao realizar o cadastro. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="step-confirmacao success-container">
        <div className="success-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
        </div>
        <h2 className="success-title">Cadastro realizado com sucesso!</h2>
        <p className="success-message">
          Obrigado por se cadastrar. Nossa equipe entrará em contato em breve para validar suas informações.
        </p>
        <CustomButton
          variant="contained"
          customBackground="#46558D"
          onClick={() => window.open('https://clube-do-banho-panel-staging-bfsrbcz6sq-uc.a.run.app/login', '_blank')}
          sx={{ fontSize: '15px', borderRadius: '0.5rem', marginTop: '24px' }}
        >
          Acessar área do parceiro
        </CustomButton>
      </div>
    );
  }

  return (
    <div className="step-confirmacao">
      <h2 className="step-confirmacao-title">Confirmação</h2>
      <p className="step-confirmacao-subtitle">Revise seus dados antes de finalizar o cadastro.</p>

      {error && <div className="error-message-box">{error}</div>}

      <div className="confirmation-summary">
        <div className="summary-section">
          <h3 className="section-title">Dados do Responsável</h3>
          <div className="info-row">
            <span className="info-label">Nome:</span>
            <span className="info-value">{formData.responsavel.nomeCompleto}</span>
          </div>
          <div className="info-row">
            <span className="info-label">CPF:</span>
            <span className="info-value">{formData.responsavel.cpf}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Email:</span>
            <span className="info-value">{formData.responsavel.email}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Telefone:</span>
            <span className="info-value">{formData.responsavel.telefone}</span>
          </div>
        </div>

        <div className="summary-section">
          <h3 className="section-title">Dados do Negócio</h3>
          <div className="info-row">
            <span className="info-label">Petshop:</span>
            <span className="info-value">{formData.negocio.nomePetshop}</span>
          </div>
          <div className="info-row">
            <span className="info-label">CNPJ:</span>
            <span className="info-value">{formData.negocio.cnpj}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Razão Social:</span>
            <span className="info-value">{formData.negocio.razaoSocial}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Telefone:</span>
            <span className="info-value">{formData.negocio.telefone}</span>
          </div>
        </div>

        <div className="summary-section">
          <h3 className="section-title">Endereço</h3>
          <div className="info-row">
            <span className="info-label">Local:</span>
            <span className="info-value">
              {formData.endereco.rua}, {formData.endereco.numero}
              {formData.endereco.complemento ? ` - ${formData.endereco.complemento}` : ''}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">Bairro/Cidade:</span>
            <span className="info-value">
              {formData.endereco.bairro} - {formData.endereco.cidade}/{formData.endereco.uf}
            </span>
          </div>
          <div className="info-row">
            <span className="info-label">CEP:</span>
            <span className="info-value">{formData.endereco.cep}</span>
          </div>
        </div>

        {formData.category.name && (
          <div className="summary-section">
            <h3 className="section-title">Plano Selecionado</h3>
            <div className="info-row">
              <span className="info-label">Plano:</span>
              <span className="info-value">{formData.category.name}</span>
            </div>
          </div>
        )}

        <div className="summary-section">
          <h3 className="section-title">Dados de Acesso</h3>
          <div className="info-row">
            <span className="info-label">Email de Login:</span>
            <span className="info-value">{formData.login.email}</span>
          </div>
          <div className="info-row">
            <span className="info-label">Senha:</span>
            <span className="info-value">********</span>
          </div>
        </div>
      </div>

      <div className="form-actions form-actions-two">
        <CustomButton
          type="button"
          variant="outlined"
          onClick={onBack}
          disabled={loading}
          sx={{
            borderColor: '#FF914D',
            color: '#FF914D',
            width: '150px',
            '&:hover': {
              borderColor: '#FF914D',
              backgroundColor: 'rgba(255, 145, 77, 0.04)',
              color: '#FF914D',
            },
          }}
        >
          Voltar
        </CustomButton>
        <CustomButton
          type="button"
          variant="contained"
          customBackground="#46558D"
          onClick={handleCadastrar}
          disabled={loading}
          sx={{ width: '150px' }}
        >
          {loading ? 'Cadastrando...' : 'Cadastrar'}
        </CustomButton>
      </div>
    </div>
  );
};

export default StepConfirmacao;
